<script lang="ts">
  import type { ChatMessageAdmin } from '@pin/schemas/socket';
  import { format } from 'date-fns';
  import { nb } from 'date-fns/locale';
  import { onMount } from 'svelte';
  import { fetchUserMessages } from '../../api/api';

  interface Props {
    // The banned or muted user and, for a parent account, its child
    // profiles: each profile's messages are stored under its own id.
    userIds: string[];
  }
  let { userIds }: Props = $props();

  // The newest messages of each user. `storeUserChatMessage` keeps a user's
  // messages for 14 days, so older ones are gone.
  const TAKE = 50;

  let messages = $state<ChatMessageAdmin[] | null>(null);
  let error = $state<string | null>(null);

  const showNames = $derived(
    messages != null && new Set(messages.map((m) => m.user.id)).size > 1
  );

  onMount(async () => {
    try {
      const perUser = await Promise.all(
        userIds.map((userId) => fetchUserMessages(userId, { take: TAKE }))
      );
      // Newest first: the messages that led to the ban or mute are usually
      // the last
      messages = perUser.flat().sort((a, b) => b.ts - a.ts);
    } catch (e) {
      error = String(e);
    }
  });
</script>

{#if error != null}
  <p class="state error">Couldn't fetch messages: {error}</p>
{:else if messages == null}
  <p class="state">Loading messages...</p>
{:else if messages.length === 0}
  <p class="state">No messages from the last 14 days</p>
{:else}
  <ul class="messages">
    {#each messages as message (message.id)}
      <li>
        <span class="meta">
          {format(message.ts, 'dd.MM.yy HH:mm:ss', { locale: nb })}
          {#if showNames}· {message.user.name}{/if}
        </span>
        <span class="text">{message.text}</span>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .state {
    margin: 0;
    font-size: 0.85rem;
    color: var(--color-zinc-400);
  }

  .error {
    color: #f87171;
  }

  .messages {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 15rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  li {
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid var(--color-zinc-700);
    padding-bottom: 0.3rem;
  }

  .meta {
    font-size: 0.7rem;
    color: var(--color-zinc-400);
  }

  .text {
    font-size: 0.85rem;
    word-break: break-word;
  }
</style>
